import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { InkyMascot } from '../brand';
import { IconTile } from './PageLayout';

/**
 * Empty state with one clear next step. Pass `inky` to show the mascot
 * (use it where Inky can actually help with the next step).
 *
 * @param {object} props
 * @param {React.ReactNode} [props.icon] Icon shown in a tile (ignored when `inky`).
 * @param {boolean} [props.inky=false] Show Inky instead of an icon.
 * @param {React.ReactNode} props.title Headline.
 * @param {React.ReactNode} [props.description] One or two sentences.
 * @param {React.ReactNode} [props.actions] Buttons (primary first).
 * @param {boolean} [props.compact=false] Smaller paddings for use inside cards.
 * @param {object} [props.sx] Extra styles.
 * @returns {JSX.Element}
 */
export function EmptyState({ icon, inky = false, title, description, actions, compact = false, sx }) {
  return (
    <Stack
      role="status"
      alignItems="center"
      spacing={1.25}
      sx={{
        textAlign: 'center',
        px: 2,
        py: compact ? 4 : { xs: 5, md: 7 },
        ...sx,
      }}
    >
      {inky ? (
        <InkyMascot size={compact ? 48 : 64} mood="idle" decorative />
      ) : (
        icon && <IconTile tone="neutral" size={compact ? 40 : 48}>{icon}</IconTile>
      )}
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, pt: 0.5 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
          {description}
        </Typography>
      )}
      {actions && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1,
            pt: 1,
          }}
        >
          {actions}
        </Box>
      )}
    </Stack>
  );
}

/**
 * Loading placeholder for lists and tables (instead of a spinner).
 *
 * @param {object} props
 * @param {number} [props.rows=5] Number of rows.
 * @param {'rows'|'cards'} [props.variant='rows'] Row list or card grid.
 * @param {string} [props.label='Loading'] Accessible label.
 * @returns {JSX.Element}
 */
export function ListSkeleton({ rows = 5, variant = 'rows', label = 'Loading' }) {
  if (variant === 'cards') {
    return (
      <Box
        role="progressbar"
        aria-label={label}
        aria-busy="true"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
          gap: 2,
        }}
      >
        {Array.from({ length: rows }).map((_, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              borderRadius: 'var(--q-radius-card, 16px)',
              p: 2.5,
            }}
          >
            <Skeleton variant="text" width="55%" height={26} />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="70%" />
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Skeleton variant="rounded" width={88} height={22} />
              <Skeleton variant="rounded" width={104} height={22} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Box role="progressbar" aria-label={label} aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: i < rows - 1 ? 1 : 0,
            borderColor: 'var(--q-row-divider)',
          }}
        >
          <Skeleton variant="rounded" width={32} height={32} sx={{ flex: 'none' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width={`${60 - (i % 3) * 10}%`} />
            <Skeleton variant="text" width={`${35 + (i % 2) * 15}%`} height={16} />
          </Box>
          <Skeleton variant="rounded" width={72} height={22} sx={{ display: { xs: 'none', sm: 'block' } }} />
        </Box>
      ))}
    </Box>
  );
}

/**
 * Confirmation dialog for destructive actions.
 *
 * @param {object} props
 * @param {boolean} props.open Whether the dialog is open.
 * @param {string} props.title Title.
 * @param {React.ReactNode} props.message Body text.
 * @param {string} [props.confirmLabel='Delete'] Confirm button label.
 * @param {boolean} [props.destructive=true] Use the error colour for confirm.
 * @param {boolean} [props.busy=false] Disable buttons while working.
 * @param {Function} props.onConfirm Confirm handler.
 * @param {Function} props.onClose Cancel handler.
 * @returns {JSX.Element}
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  destructive = true,
  busy = false,
  onConfirm,
  onClose,
}) {
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth disableRestoreFocus>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          disabled={busy}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
